/**
 * Settings → Audit Trail Configuration — FULL legacy parity (owner request 2026-10-12).
 *
 * Ported 1:1 from ALPACA's AuditTrailToggleControl screen. The grid is backed by the REAL
 * shared table `dbo.AuditTrailToggleControl` (via GET/PUT /api/v1/audit-toggles), one row per
 * auditable form (Module/BO/Path from the joined dbo.DefaultSetting), with four toggles:
 *   • Audit Trail    — AuditTrailToggle    (master on/off for that form's audit)
 *   • Reason Required — ReasonRequiredToggle (force a reason on edit/delete)
 *   • Default         — Default             (the legacy "shown by default" flag)
 *   • Retention Days  — RecordRetentionDays (lookup, e.g. "1Y"; display-only, not enforced)
 *
 * Two modes mirror the legacy ListViews:
 *   • VIEW  (AuditTrailToggleControl_ListView)      — read-only; rows filtered to
 *     [Default]=true OR [AuditTrail]=true (~60); shows ONLY the Reason Required toggle column.
 *   • EDIT  (AuditTrailToggleControl_ListView_Edit) — all 365 rows; shows all four toggle
 *     columns, all editable; Save writes them back to the table.
 *
 * (This replaces the earlier build that derived rows from the nav tree + AI_AppSettings, which
 * showed the wrong data — every form defaulting to audit=ON.)
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu } from '../accounting/ExportMenu';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { useFetchMs } from '../../hooks/useFetchMs';
import '../backlog/backlog.css';

/** One grid row = one dbo.AuditTrailToggleControl row (joined to DefaultSetting). */
interface ToggleRow {
  oid: string;
  moduleName: string;   // DefaultSetting.ModuleName
  bo: string;           // DefaultSetting.NavigationItemNameID — the legacy "BO" column
  navItemName: string;  // DefaultSetting.NavigationItemName (not shown; ≈ audit FormName)
  path: string;         // DefaultSetting.ItemPathbyCaption
  auditTrail: boolean;
  reasonRequired: boolean;
  isDefault: boolean;
  retention: string | null;
}
interface RetentionOption { id: string; label: string }

/** G5's caption for "no retention set" — XAF's null lookup item, not a table row. Saved as empty
 *  (the API stores a NULL RecordRetentionDays; AuditToggleRetention on the server). */
const RETENTION_NA = 'N/A';

export default function AuditTrailConfigurationPage({ onHome }: { onHome?: () => void }) {
  // View-then-Edit gate (BO UI Standard v2): opens READ-ONLY (filtered VIEW list, Reason
  // Required only); the Edit button unlocks all rows + all four toggle columns; Save persists
  // the whole grid; Cancel reverts to the last-loaded snapshot.
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [rows, setRows] = useState<ToggleRow[]>([]);
  const [snapshot, setSnapshot] = useState<ToggleRow[]>([]);
  const [retentionOptions, setRetentionOptions] = useState<RetentionOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const loadMs = useFetchMs(loading);
  const [error, setError] = useState<string | null>(null);
  // Search + page-level grid maximize (search-row zoom), mirroring ADC / ModuleListView.
  const [search, setSearch] = useState('');
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);

  const load = useMemo(() => async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/v1/audit-toggles');
      const data = (res.data as { data?: { rows?: ToggleRow[]; retentionOptions?: RetentionOption[] } })?.data;
      // A row with no retention stays empty (shown as "N/A", like G5) — it used to be defaulted to
      // '1Y' here, so a Save silently wrote 1Y into rows G5 shows as N/A (owner report 2026-11-03).
      const loaded = (data?.rows ?? []).map(r => ({ ...r, retention: r.retention ?? '' }));
      setRows(loaded);
      setSnapshot(loaded);
      setRetentionOptions(data?.retentionOptions ?? []);
      // Clear any row / select-all selection on every (re)load — initial, after Save
      // (persistAll calls load), and Refresh — so the header check-all box does not
      // stay ticked once the save returns to View mode (owner report 2026-10-14).
      setSelectedIds(new Set());
    } catch {
      setError('Could not load audit trail configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // View and Edit are different lists (View shows only audited / default forms), so a row ticked
  // in one must not stay ticked in the other: clear the selection on every switch (owner report
  // 2026-11-03 — InvoiceDelivery ticked in View was still ticked after Edit).
  useEffect(() => { setSelectedIds(new Set()); }, [ro]);

  const setRow = (oid: string, patch: Partial<ToggleRow>) =>
    setRows(rs => rs.map(r => (r.oid === oid ? { ...r, ...patch } : r)));

  // VIEW list filter (legacy criteria "[Default]=True Or [AuditTrailToggle]=True"); EDIT shows all.
  const displayRows = useMemo(
    () => (ro ? rows.filter(r => r.isDefault || r.auditTrail) : rows),
    [ro, rows],
  );

  /** Persist the whole grid (the four editable toggles per row) back to the legacy table. */
  const persistAll = async () => {
    const payload = rows.map(r => ({
      oid: r.oid,
      auditTrail: r.auditTrail,
      reasonRequired: r.reasonRequired,
      isDefault: r.isDefault,
      retention: r.retention,
    }));
    await api.put('/api/v1/audit-toggles', { rows: payload });
    await load();
  };

  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Audit Trail Configuration',
      successMessage: 'Audit trail configuration saved successfully.',
      save: persistAll,
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change audit trail configuration.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  const handleCancel = () => gate.cancel(() => { setRows(snapshot); setSelectedIds(new Set()); });

  /** Centered checkbox cell — stops row-click so toggling never selects the row. */
  const checkCell = (checked: boolean, onToggle: () => void) => (
    <span onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
      <input type="checkbox" checked={checked} onChange={onToggle} disabled={ro} />
    </span>
  );

  const idCols: AppColumn<ToggleRow>[] = [
    { field: 'moduleName', header: 'ModuleName', width: 160, text: r => r.moduleName },
    { field: 'bo',         header: 'BO',          width: 190, text: r => r.bo },
    { field: 'path',       header: 'Path',        text: r => r.path },
  ];
  const reasonCol: AppColumn<ToggleRow> = {
    // Toggle columns take G5's captions (no spaces) and its header filter (Yes / No) — owner request 2026-11-03.
    field: 'reasonRequired', header: 'ReasonRequired', width: 130, align: 'center', sortable: false,
    text: r => (r.reasonRequired ? 'Yes' : 'No'),
    render: r => checkCell(r.reasonRequired, () => setRow(r.oid, { reasonRequired: !r.reasonRequired })),
  };
  // VIEW = Module/BO/Path + Reason Required only (legacy AuditTrailToggleControl_ListView).
  // EDIT = Module/BO/Path + Audit Trail · Reason Required · Default · Retention Days (all editable).
  const columns: AppColumn<ToggleRow>[] = ro
    ? [...idCols, reasonCol]
    : [
        ...idCols,
        { field: 'auditTrail', header: 'AuditTrail', width: 110, align: 'center', sortable: false,
          text: r => (r.auditTrail ? 'Yes' : 'No'),
          render: r => checkCell(r.auditTrail, () => setRow(r.oid, { auditTrail: !r.auditTrail })) },
        reasonCol,
        { field: 'isDefault', header: 'Default', width: 90, align: 'center', sortable: false,
          text: r => (r.isDefault ? 'Yes' : 'No'),
          render: r => checkCell(r.isDefault, () => setRow(r.oid, { isDefault: !r.isDefault })) },
        { field: 'retention', header: 'RetentionDays', width: 130, align: 'center', sortable: false,
          text: r => r.retention || RETENTION_NA,
          render: r => (
            <span onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
              <select value={r.retention ?? ''} disabled={ro} onChange={e => setRow(r.oid, { retention: e.target.value })}>
                {/* G5's lookup lists "N/A" first = no retention set (saved as empty). */}
                <option value="">{RETENTION_NA}</option>
                {retentionOptions.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
              </select>
            </span>
          ) },
      ];

  // Search filters the visible rows; the filtered set feeds BOTH the grid and the export.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return displayRows;
    return displayRows.filter(r => [r.moduleName, r.bo, r.path].some(v => (v ?? '').toLowerCase().includes(q)));
  }, [displayRows, search]);
  const exportMatrix = useMemo(() => gridExportMatrix(visibleRows, columns), [visibleRows, columns]);

  return (
    <div className="bl-page">
      <div className="bl-grid-area">
        {/* Breadcrumb + the (empty) FormTitle on ONE row (owner request 2026-10-14): FormTitle
            stays MOUNTED so it keeps publishing the load-time chip to the toolbar, but no longer
            sits on its own empty line above the toolbar (.mlv-toolbar-row is a block, so the empty
            .ft-chips was an ~18px blank strip). Breadcrumb hidden while the grid is maximized.
            Home › Settings › Audit & Logs › Audit Trail Configuration. */}
        {!gridMax && (
          <div className="dp-breadcrumb" style={{ padding: '6px 0 2px' }}>
            <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span>Audit &amp; Logs</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Audit Trail Configuration</span>
            <FormTitle pageId="audittrailcfg" loadMs={loadMs} loading={loading} />
          </div>
        )}
        {!gridMax && (
          <div className="mlv-toolbar-row">
            <AppToolbar
              left={
                <EditGateActions
                  gate={gate}
                  saving={saving}
                  disabled={loading}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              }
              onRefresh={() => void load()}
              refreshing={loading}
              refreshDisabled={gate.editing}
            />
          </div>
        )}

        {/* Search + Export + Maximize on ONE row above the grid card (ADC / ModuleListView
            layout, owner request 2026-10-14) — the grid's own header tools are hidden, so this
            is the single place to search, export and zoom. Stays visible while maximized. */}
        {/* In maximized mode the breadcrumb + toolbar are hidden, so the search row is
            the top strip: nudge it down 4px and trim 3px below (base .list-search-row is
            0/10) so it is not jammed under the top bar (owner request 2026-10-14). */}
        <div className="list-search-row" style={gridMax ? { padding: '4px 0 7px' } : undefined}>
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search module, BO, path…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="AuditTrailConfiguration"
              headers={exportMatrix.headers} rows={exportMatrix.rows} />
            <button className={`dv-gridact${gridMax ? ' dv-on' : ''}`} onClick={() => setGridMax(v => !v)}
              title={gridMax ? 'Restore' : 'Maximize'} aria-label={gridMax ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          {error && (
            <div style={{ margin: '10px 20px 0', padding: '8px 12px', borderRadius: 6,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13 }}>
              <i className="ti ti-alert-circle" style={{ marginRight: 6, verticalAlign: 'middle' }} />{error}
            </div>
          )}

          <AppDataGrid<ToggleRow>
            rows={visibleRows}
            columns={columns}
            getRowId={r => r.oid}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            loading={loading}
            emptyText="No auditable forms configured."
            hideHeaderTools
          />
        </div>
      </div>
    </div>
  );
}
